import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, GraduationCap, BookOpen, Briefcase, Building2, DollarSign, Shield, ChevronRight, MapPin } from 'lucide-react';
import SmartProfileEvalCTA from './components/SmartProfileEvalCTA';
import { usePageMeta } from '@/hooks/useSEO';

const quickLinks = [
  { label: 'Australia Student Visa', path: '/study-abroad/australia/student-visa/', icon: Shield },
  { label: 'Scholarships', path: '/study-abroad/australia/scholarships/', icon: GraduationCap },
  { label: 'PR After Study', path: '/study-abroad/australia/pr-after-study/', icon: CheckCircle },
];

const whyAustralia = [
  { title: 'Post-Study Work Visa (2-4 Years)', desc: 'Depending on your qualification level, you can stay 2 to 4 years after graduation on a Temporary Graduate visa (subclass 485).' },
  { title: 'World-Class Universities', desc: 'Home to 7 of the world\'s top 100 universities, including ANU, Melbourne, Sydney, and UNSW.' },
  { title: 'High Employability', desc: 'Australia has low unemployment and high demand for skilled workers in healthcare, IT, engineering, and trades.' },
  { title: 'PR Points System', desc: 'Australian PR uses a transparent points-based system. Study in Australia gives you bonus points for age, education, and regional study.' },
  { title: 'Quality of Life', desc: 'Consistently ranked among the top countries for quality of life, safety, and standard of living.' },
  { title: 'Work While Studying', desc: 'International students can work up to 48 hours per fortnight during semesters and unlimited during breaks.' },
];

const topCities = [
  { name: 'Melbourne', state: 'Victoria', highlight: 'World\'s Most Liveable City' },
  { name: 'Sydney', state: 'New South Wales', highlight: 'Global Business Hub' },
  { name: 'Brisbane', state: 'Queensland', highlight: 'Growing Tech Scene' },
  { name: 'Perth', state: 'Western Australia', highlight: 'Mining & Engineering Hub' },
  { name: 'Adelaide', state: 'South Australia', highlight: 'Regional Study Benefits' },
  { name: 'Canberra', state: 'ACT', highlight: 'Capital & Research Focus' },
];

const AustraliaHubPage = () => {
  usePageMeta(
    'Study in Australia 2026: Universities, Visa & PR Pathways | Zafco',
    'Complete guide to studying in Australia in 2026: top universities, Subclass 500 student visa, scholarships, post-study work visa (2-4 years), PR pathways & top cities. MEA-approved counsellors.',
  );
  return (
    <div data-testid="sa-australia-hub">
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #0891B2 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-80 h-80 bg-[#FFD700] rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <span className="text-lg">{getFlagEmoji('AU')}</span>
              <span className="text-xs text-white/90 font-medium">Study Destination</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-syne text-white leading-tight mb-5">
              Study in <span className="text-[#FFD700]">Australia</span>
            </h1>
            <p className="text-base lg:text-lg text-slate-300 leading-relaxed mb-8 max-w-2xl">
              Australia offers world-class education, a transparent PR points system, and up to
              4 years of post-study work rights. Home to 7 of the top 100 universities globally.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/study-abroad/book-counselling/"
                className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-8 py-3.5 rounded-full transition-all sa-pulse-cta"
              >
                Get Free Australia Counselling <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/study-abroad/free-profile-evaluation/"
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-sm px-8 py-3.5 rounded-full transition-all"
              >
                Check Eligibility
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-[#001F5C] py-8">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { val: '750K+', label: 'International Students' },
            { val: '2-4 Yrs', label: 'Post-Study Work Visa' },
            { val: '7', label: 'Unis in Global Top 100' },
            { val: '65 pts', label: 'Min PR Points Required' },
          ].map((s) => (
            <div key={s.label} className="sa-stat-card rounded-xl p-4 text-center">
              <div className="text-2xl font-bold font-syne text-[#C5A028]">{s.val}</div>
              <div className="text-xs text-slate-400 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="sa-gold-line mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#003087] mb-3">
              Study in Australia — Everything You Need
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {quickLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="sa-feature-card bg-white rounded-xl p-5 flex items-start gap-4 group"
              >
                <div className="w-10 h-10 rounded-lg bg-[#003087]/10 flex items-center justify-center shrink-0">
                  <link.icon className="w-5 h-5 text-[#003087]" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 group-hover:text-[#003087] transition-colors">{link.label}</h3>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#003087] mt-1 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Australia */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="sa-gold-line mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#003087] mb-3">Why Study in Australia?</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyAustralia.map((item) => (
              <div key={item.title} className="bg-white rounded-xl p-6 border border-slate-100">
                <h3 className="text-base font-bold font-syne text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Study Cities */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="sa-gold-line mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#003087] mb-3">Top Study Cities</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {topCities.map((city) => (
              <div key={city.name} className="sa-feature-card bg-white rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-4 h-4 text-[#003087]" />
                  <h3 className="text-sm font-bold text-slate-900">{city.name}</h3>
                </div>
                <p className="text-xs text-slate-500">{city.state}</p>
                <span className="inline-block mt-2 text-xs text-[#C5A028] font-medium">{city.highlight}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Smart Profile-Eval CTA (cross-link to existing free-profile-evaluation tool) */}
      <SmartProfileEvalCTA country="Australia" country_slug="australia-hub" testId="australia-hub-smart-cta" />

      {/* CTA */}
      <section className="sa-hero-gradient py-14">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold font-syne text-white mb-4">Start Your Australia Study Journey</h2>
          <p className="text-base text-slate-300 mb-8 max-w-2xl mx-auto">
            Get personalized guidance on university selection, student visa, and PR pathways for Australia.
          </p>
          <Link
            to="/study-abroad/book-counselling/"
            className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-8 py-3.5 rounded-full transition-all"
          >
            Book Free Australia Counselling <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

function getFlagEmoji(countryCode) {
  const codePoints = countryCode.toUpperCase().split('').map((c) => 127397 + c.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

export default AustraliaHubPage;
